/**
 * TanStack Query hooks for API data fetching.
 *
 * These hooks provide caching, background refetching, and optimistic updates
 * for all API endpoints. Use these instead of the basic useApi hook for
 * automatic stale-while-revalidate behavior.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { get, post, put, del } from '@/api/client';
import type {
  MonitoringMetrics,
  ExtendedMonitoringMetrics,
  DocumentQualityMetrics,
  CostMetrics,
  CloudWatchConfig,
  DocumentListResponse,
  Document,
  DocumentState,
  SourceType,
  PriorityListResponse,
  PriorityItem,
  PriorityStatus,
  CreatePriorityItem,
  UpdatePriorityItem,
  ImportCSVResponse,
  TaskLogListResponse,
  TaskState,
  ActivityLogListResponse,
  ActivityStage,
  PriorityHistoryResponse,
  UpdateDocumentMetadata,
  UpdateDocumentNotes,
  GitHubConfig,
  GitHubConnectResponse,
  GitHubTestResult,
  ReposSummary,
  ContributorsResponse,
  RepoPRListResponse,
  ReposConfig,
  ReposConfigUpdate,
  OrgRepo,
  WeekGoalsResponse,
  WeekSummaryResponse,
  GoalLane,
  Goal,
  DashboardUser,
  CreateLaneRequest,
  UpdateLaneRequest,
  CreateGoalRequest,
  UpdateGoalRequest,
  CarryOverRequest,
  ReorderRequest,
  MetricHistoryResponse,
} from '@/api/types';

// ============================================================================
// Query Keys
// ============================================================================
// Centralized query keys for consistent cache invalidation

export const queryKeys = {
  // System
  systemMetrics: ['system', 'metrics'] as const,

  // Monitoring
  metrics: ['metrics'] as const,
  extendedMetrics: ['metrics', 'extended'] as const,
  documentQuality: ['metrics', 'documentQuality'] as const,
  costMetrics: ['metrics', 'costs'] as const,
  cloudWatchConfig: ['cloudwatch', 'config'] as const,
  taskLogs: (filters: TaskLogFilters) => ['taskLogs', filters] as const,
  activityLogs: (filters: ActivityLogFilters) =>
    ['activityLogs', filters] as const,

  // Documents
  documents: (filters: DocumentFilters) => ['documents', filters] as const,
  document: (id: string) => ['document', id] as const,
  documentAuditLog: (id: string) => ['document', id, 'audit'] as const,

  // Priority
  priorityItems: (filters: PriorityFilters) =>
    ['priorityItems', filters] as const,
  priorityItem: (id: string) => ['priorityItem', id] as const,
  priorityHistory: (id: string) => ['priorityItem', id, 'history'] as const,

  // GitHub / Repos
  githubConfig: ['github', 'config'] as const,
  reposSummary: ['repos', 'summary'] as const,
  reposConfig: ['repos', 'config'] as const,
  reposContributors: ['repos', 'contributors'] as const,
  repoPrs: (owner: string, name: string, state: string) =>
    ['repos', owner, name, 'prs', state] as const,
  orgRepos: ['repos', 'org', 'discover'] as const,
  metricHistory: (metric: string, days: number) =>
    ['repos', 'metrics', 'history', metric, days] as const,

  // Goals
  goalsWeek: (week: string) => ['goals', 'week', week] as const,
  goalsWeekSummary: (week: string) =>
    ['goals', 'week', week, 'summary'] as const,
  goalsUsers: ['goals', 'users'] as const,
} as const;

// ============================================================================
// Filter Types
// ============================================================================

export interface SystemMetrics {
  cpu_percent: number;
  memory_percent: number;
  memory_used_gb: number;
  memory_total_gb: number;
  disk_percent: number;
  disk_used_gb: number;
  disk_total_gb: number;
  load_average: number[];
}

interface TaskLogFilters {
  taskName?: string;
  state?: TaskState;
  worker?: string;
  startTime?: string;
  endTime?: string;
  limit?: number;
  offset?: number;
}

interface ActivityLogFilters {
  taskId?: string;
  stage?: ActivityStage;
  action?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

interface DocumentFilters {
  page: number;
  pageSize?: number;
  search?: string;
  state?: DocumentState | '';
  sourceType?: SourceType | '';
}

interface PriorityFilters {
  page: number;
  pageSize?: number;
  status?: PriorityStatus | '';
}

// ============================================================================
// System Hooks
// ============================================================================

/**
 * Fetch system resource usage metrics (CPU, memory, disk).
 * Automatically refreshes every 30 seconds.
 */
export function useSystemMetrics(
  options?: Omit<UseQueryOptions<SystemMetrics>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.systemMetrics,
    queryFn: () => get<SystemMetrics>('/system'),
    refetchInterval: 30 * 1000,
    staleTime: 15 * 1000,
    ...options,
  });
}

// ============================================================================
// Monitoring Hooks
// ============================================================================

/**
 * Fetch monitoring dashboard metrics.
 * Automatically refreshes every 30 seconds.
 */
export function useMetrics(
  options?: Omit<UseQueryOptions<MonitoringMetrics>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.metrics,
    queryFn: () => get<MonitoringMetrics>('/integrations/monitoring/metrics'),
    refetchInterval: 30 * 1000, // Refresh every 30 seconds
    ...options,
  });
}

/**
 * Fetch extended monitoring metrics for detailed dashboard.
 */
export function useExtendedMetrics(
  options?: Omit<
    UseQueryOptions<ExtendedMonitoringMetrics>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: queryKeys.extendedMetrics,
    queryFn: () =>
      get<ExtendedMonitoringMetrics>(
        '/integrations/monitoring/metrics/extended'
      ),
    ...options,
  });
}

/**
 * Fetch CloudWatch dashboard configuration.
 */
export function useCloudWatchConfig(
  options?: Omit<UseQueryOptions<CloudWatchConfig>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.cloudWatchConfig,
    queryFn: () => get<CloudWatchConfig>('/integrations/monitoring/cloudwatch'),
    staleTime: 5 * 60 * 1000, // CloudWatch config rarely changes
    ...options,
  });
}

/**
 * Fetch document quality metrics.
 * Shows breakdown of discovered documents by type and state.
 */
export function useDocumentQualityMetrics(
  options?: Omit<
    UseQueryOptions<DocumentQualityMetrics>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: queryKeys.documentQuality,
    queryFn: () =>
      get<DocumentQualityMetrics>('/integrations/monitoring/documents/quality'),
    refetchInterval: 60 * 1000, // Refresh every minute
    ...options,
  });
}

/**
 * Fetch cost tracking metrics.
 * Shows estimated LLM and API costs by time period.
 */
export function useCostMetrics(
  options?: Omit<UseQueryOptions<CostMetrics>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.costMetrics,
    queryFn: () => get<CostMetrics>('/integrations/monitoring/costs'),
    refetchInterval: 60 * 1000, // Refresh every minute
    ...options,
  });
}

/**
 * Fetch task logs with filters.
 */
export function useTaskLogs(
  filters: TaskLogFilters = {},
  options?: Omit<UseQueryOptions<TaskLogListResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.taskLogs(filters),
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.taskName) params.append('task_name', filters.taskName);
      if (filters.state) params.append('state', filters.state);
      if (filters.worker) params.append('worker', filters.worker);
      if (filters.startTime) params.append('start_time', filters.startTime);
      if (filters.endTime) params.append('end_time', filters.endTime);
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.offset) params.append('offset', filters.offset.toString());

      return get<TaskLogListResponse>(
        `/integrations/monitoring/workers/logs?${params.toString()}`
      );
    },
    ...options,
  });
}

/**
 * Fetch activity logs with filters.
 */
export function useActivityLogs(
  filters: ActivityLogFilters = {},
  options?: Omit<
    UseQueryOptions<ActivityLogListResponse>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: queryKeys.activityLogs(filters),
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.taskId) params.append('task_id', filters.taskId);
      if (filters.stage) params.append('stage', filters.stage);
      if (filters.action) params.append('action', filters.action);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.offset) params.append('offset', filters.offset.toString());

      return get<ActivityLogListResponse>(
        `/integrations/monitoring/activities?${params.toString()}`
      );
    },
    ...options,
  });
}

// ============================================================================
// Document Hooks
// ============================================================================

/**
 * Fetch paginated document list with filters.
 */
export function useDocuments(
  filters: DocumentFilters,
  options?: Omit<UseQueryOptions<DocumentListResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.documents(filters),
    queryFn: () => {
      const params = new URLSearchParams();
      params.append('page', filters.page.toString());
      params.append('page_size', (filters.pageSize ?? 20).toString());
      if (filters.search) params.append('search', filters.search);
      if (filters.state) params.append('state', filters.state);
      if (filters.sourceType) params.append('source_type', filters.sourceType);

      return get<DocumentListResponse>(`/documents?${params.toString()}`);
    },
    ...options,
  });
}

/**
 * Fetch a single document by ID.
 */
export function useDocument(
  id: string,
  options?: Omit<UseQueryOptions<Document>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.document(id),
    queryFn: () => get<Document>(`/documents/${id}`),
    enabled: !!id,
    ...options,
  });
}

/**
 * Mutation to verify a document.
 */
export function useVerifyDocument(
  options?: UseMutationOptions<Document, Error, { id: string; notes: string }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }) =>
      post<Document>(`/documents/${id}/verify`, { notes }),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.document(id) });
      queryClient.invalidateQueries({
        queryKey: ['documents'],
        exact: false,
      });
    },
    ...options,
  });
}

/**
 * Mutation to reject a document.
 */
export function useRejectDocument(
  options?: UseMutationOptions<Document, Error, { id: string; notes: string }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }) =>
      post<Document>(`/documents/${id}/reject`, { notes }),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.document(id) });
      queryClient.invalidateQueries({
        queryKey: ['documents'],
        exact: false,
      });
    },
    ...options,
  });
}

/**
 * Mutation to quarantine a document.
 */
export function useQuarantineDocument(
  options?: UseMutationOptions<Document, Error, { id: string; notes: string }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }) =>
      post<Document>(`/documents/${id}/quarantine`, { notes }),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.document(id) });
      queryClient.invalidateQueries({
        queryKey: ['documents'],
        exact: false,
      });
    },
    ...options,
  });
}

/**
 * Mutation to update document metadata.
 */
export function useUpdateDocumentMetadata(
  options?: UseMutationOptions<
    Document,
    Error,
    { id: string; metadata: UpdateDocumentMetadata }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, metadata }) =>
      put<Document>(`/documents/${id}/metadata`, metadata),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.document(id) });
      queryClient.invalidateQueries({
        queryKey: ['documents'],
        exact: false,
      });
    },
    ...options,
  });
}

/**
 * Mutation to update document notes.
 */
export function useUpdateDocumentNotes(
  options?: UseMutationOptions<
    Document,
    Error,
    { id: string; notes: UpdateDocumentNotes }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }) =>
      put<Document>(`/documents/${id}/notes`, notes),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.document(id) });
    },
    ...options,
  });
}

// ============================================================================
// Priority Hooks
// ============================================================================

/**
 * Fetch paginated priority items with filters.
 */
export function usePriorityItems(
  filters: PriorityFilters,
  options?: Omit<UseQueryOptions<PriorityListResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.priorityItems(filters),
    queryFn: () => {
      const params = new URLSearchParams();
      params.append('page', filters.page.toString());
      params.append('page_size', (filters.pageSize ?? 20).toString());
      if (filters.status) params.append('status', filters.status);

      return get<PriorityListResponse>(
        `/integrations/priority?${params.toString()}`
      );
    },
    ...options,
  });
}

/**
 * Fetch priority item history.
 */
export function usePriorityHistory(
  id: string,
  options?: Omit<
    UseQueryOptions<PriorityHistoryResponse>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: queryKeys.priorityHistory(id),
    queryFn: () =>
      get<PriorityHistoryResponse>(`/integrations/priority/${id}/history`),
    enabled: !!id,
    ...options,
  });
}

/**
 * Mutation to create a priority item.
 */
export function useCreatePriorityItem(
  options?: UseMutationOptions<PriorityItem, Error, CreatePriorityItem>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (item) => post<PriorityItem>('/integrations/priority', item),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['priorityItems'],
        exact: false,
      });
    },
    ...options,
  });
}

/**
 * Mutation to update a priority item.
 */
export function useUpdatePriorityItem(
  options?: UseMutationOptions<
    PriorityItem,
    Error,
    { id: string; updates: UpdatePriorityItem }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }) =>
      put<PriorityItem>(`/integrations/priority/${id}`, updates),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.priorityItem(id) });
      queryClient.invalidateQueries({
        queryKey: ['priorityItems'],
        exact: false,
      });
    },
    ...options,
  });
}

/**
 * Mutation to update priority item status (optimistic update).
 */
export function useUpdatePriorityStatus(
  options?: UseMutationOptions<
    PriorityItem,
    Error,
    { id: string; status: PriorityStatus },
    { previousData: PriorityListResponse | undefined }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }) =>
      put<PriorityItem>(`/integrations/priority/${id}`, { status }),
    // Optimistic update
    onMutate: async ({ id, status }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['priorityItems'],
        exact: false,
      });

      // Snapshot the previous value
      const queries = queryClient.getQueriesData<PriorityListResponse>({
        queryKey: ['priorityItems'],
      });

      // Optimistically update all matching queries
      queries.forEach(([queryKey, data]) => {
        if (data) {
          queryClient.setQueryData<PriorityListResponse>(queryKey, {
            ...data,
            items: data.items.map((item) =>
              item.id === id ? { ...item, status } : item
            ),
          });
        }
      });

      return { previousData: queries[0]?.[1] };
    },
    onError: (_err, _vars, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.invalidateQueries({
          queryKey: ['priorityItems'],
          exact: false,
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['priorityItems'],
        exact: false,
      });
    },
    ...options,
  });
}

/**
 * Mutation to delete a priority item.
 */
export function useDeletePriorityItem(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => del(`/integrations/priority/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['priorityItems'],
        exact: false,
      });
    },
    ...options,
  });
}

/**
 * Mutation to trigger a crawl for a priority item.
 */
export function useTriggerCrawl(
  options?: UseMutationOptions<void, Error, string>
) {
  return useMutation({
    mutationFn: (id) => post(`/integrations/priority/${id}/trigger`, {}),
    ...options,
  });
}

/**
 * Mutation to import CSV.
 */
export function useImportCSV(
  options?: UseMutationOptions<
    ImportCSVResponse,
    Error,
    { csvContent: string; dryRun: boolean }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ csvContent, dryRun }) =>
      post<ImportCSVResponse>('/integrations/priority/import-csv', {
        csv_content: csvContent,
        dry_run: dryRun,
      }),
    onSuccess: (_data, { dryRun }) => {
      // Only invalidate if not a dry run
      if (!dryRun) {
        queryClient.invalidateQueries({
          queryKey: ['priorityItems'],
          exact: false,
        });
      }
    },
    ...options,
  });
}

/**
 * Mutation to rollback a priority item to a previous version.
 */
export function useRollbackPriorityItem(
  options?: UseMutationOptions<
    PriorityItem,
    Error,
    { id: string; targetVersion: number; notes?: string }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, targetVersion, notes }) =>
      post<PriorityItem>(`/integrations/priority/${id}/rollback`, {
        target_version: targetVersion,
        notes,
      }),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.priorityItem(id) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.priorityHistory(id),
      });
      queryClient.invalidateQueries({
        queryKey: ['priorityItems'],
        exact: false,
      });
    },
    ...options,
  });
}

// ============================================================================
// GitHub / Repos Hooks
// ============================================================================

/**
 * Fetch the current user's GitHub connection status.
 */
export function useGitHubConfig(
  options?: Omit<UseQueryOptions<GitHubConfig>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.githubConfig,
    queryFn: () => get<GitHubConfig>('/user/settings/github'),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Mutation to connect a GitHub account with a PAT.
 */
export function useConnectGitHub(
  options?: UseMutationOptions<GitHubConnectResponse, Error, { pat: string }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pat }) =>
      put<GitHubConnectResponse>('/user/settings/github', { pat }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.githubConfig });
    },
    ...options,
  });
}

/**
 * Mutation to test the stored GitHub connection.
 */
export function useTestGitHubConnection(
  options?: UseMutationOptions<GitHubTestResult, Error, void>
) {
  return useMutation({
    mutationFn: () => post<GitHubTestResult>('/user/settings/github/test', {}),
    ...options,
  });
}

/**
 * Mutation to disconnect GitHub account.
 */
export function useDisconnectGitHub(
  options?: UseMutationOptions<void, Error, void>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => del<void>('/user/settings/github'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.githubConfig });
      queryClient.invalidateQueries({ queryKey: queryKeys.reposSummary });
    },
    ...options,
  });
}

/**
 * Fetch the consolidated repos summary (health + attention items).
 *
 * The endpoint never blocks on GitHub API calls. When the cache is
 * stale it returns existing data with refreshing=true and kicks off
 * a background Celery task. While a refresh is in progress we poll
 * at 10 s so the UI picks up new data quickly without flooding the
 * API — a typical refresh takes ~30-40 s, so 3-4 polls cover it.
 */
export function useReposSummary(
  options?: Omit<UseQueryOptions<ReposSummary>, 'queryKey' | 'queryFn'>
) {
  const query = useQuery({
    queryKey: queryKeys.reposSummary,
    queryFn: () => get<ReposSummary>('/repos/summary'),
    refetchInterval: (query) => {
      const refreshing = query.state.data?.refreshing;
      return refreshing ? 10_000 : 60_000;
    },
    staleTime: 30 * 1000,
    ...options,
  });
  return query;
}

/**
 * Fetch contributor stats across all monitored repos.
 * Populated as a side effect of the summary computation.
 */
export function useContributors(
  options?: Omit<UseQueryOptions<ContributorsResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.reposContributors,
    queryFn: () => get<ContributorsResponse>('/repos/contributors'),
    refetchInterval: 60 * 1000,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Fetch PRs for a specific repository.
 */
export function useRepoPrs(
  owner: string,
  name: string,
  state: string = 'open',
  options?: Omit<UseQueryOptions<RepoPRListResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.repoPrs(owner, name, state),
    queryFn: () => {
      const params = new URLSearchParams();
      params.append('state', state);
      return get<RepoPRListResponse>(
        `/repos/${owner}/${name}/prs?${params.toString()}`
      );
    },
    enabled: !!owner && !!name,
    ...options,
  });
}

/**
 * Fetch metric time series history.
 */
export function useMetricHistory(
  metric: string,
  days: number = 90,
  options?: Omit<UseQueryOptions<MetricHistoryResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.metricHistory(metric, days),
    queryFn: () =>
      get<MetricHistoryResponse>(
        `/repos/metrics/history?metric=${metric}&days=${days}`
      ),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Mutation to manually refresh cached GitHub data.
 */
export function useRefreshRepos(
  options?: UseMutationOptions<void, Error, void>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => post<void>('/repos/refresh', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reposSummary });
    },
    ...options,
  });
}

/**
 * Fetch the global repos configuration (admin).
 */
export function useReposConfig(
  options?: Omit<UseQueryOptions<ReposConfig>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.reposConfig,
    queryFn: () => get<ReposConfig>('/repos/config'),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Mutation to update the global repos configuration (admin).
 */
export function useUpdateReposConfig(
  options?: UseMutationOptions<ReposConfig, Error, ReposConfigUpdate>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (update) => put<ReposConfig>('/repos/config', update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reposConfig });
      // Clear backend cache so next fetch gets fresh data with new config
      post('/repos/refresh', {}).catch(() => {});
      queryClient.invalidateQueries({ queryKey: queryKeys.reposSummary });
    },
    ...options,
  });
}

/**
 * Mutation to discover repos in the configured org.
 */
export function useDiscoverOrgRepos(
  options?: UseMutationOptions<OrgRepo[], Error, void>
) {
  return useMutation({
    mutationFn: () => post<OrgRepo[]>('/repos/config/discover', {}),
    ...options,
  });
}

// ============================================================================
// Goals Hooks
// ============================================================================

export function useWeekGoals(
  week: string,
  options?: Omit<UseQueryOptions<WeekGoalsResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.goalsWeek(week),
    queryFn: () =>
      get<WeekGoalsResponse>(`/goals/week?week=${encodeURIComponent(week)}`),
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useGoalsUsers(
  options?: Omit<UseQueryOptions<DashboardUser[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.goalsUsers,
    queryFn: () => get<DashboardUser[]>('/goals/users'),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useCreateLane(week: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLaneRequest) =>
      post<GoalLane>(`/goals/week/${week}/lanes`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.goalsWeek(week) });
    },
  });
}

export function useUpdateLane(week: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      laneId,
      body,
    }: {
      laneId: string;
      body: UpdateLaneRequest;
    }) => put<GoalLane>(`/goals/week/${week}/lanes/${laneId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.goalsWeek(week) });
    },
  });
}

export function useDeleteLane(week: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (laneId: string) =>
      del<void>(`/goals/week/${week}/lanes/${laneId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.goalsWeek(week) });
    },
  });
}

export function useCreateGoal(week: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateGoalRequest) =>
      post<Goal>(`/goals/week/${week}/goals`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.goalsWeek(week) });
    },
  });
}

export function useUpdateGoal(week: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      goalId,
      body,
    }: {
      goalId: string;
      body: UpdateGoalRequest;
    }) => put<Goal>(`/goals/week/${week}/goals/${goalId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.goalsWeek(week) });
    },
  });
}

export function useDeleteGoal(week: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (goalId: string) =>
      del<void>(`/goals/week/${week}/goals/${goalId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.goalsWeek(week) });
    },
  });
}

export function useWeekSummary(
  week: string,
  options?: Omit<UseQueryOptions<WeekSummaryResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.goalsWeekSummary(week),
    queryFn: () =>
      get<WeekSummaryResponse>(
        `/goals/week/${encodeURIComponent(week)}/summary`
      ),
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useCarryOverGoals(sourceWeek: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CarryOverRequest) =>
      post<Goal[]>(`/goals/week/${sourceWeek}/carry-over`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.goalsWeek(sourceWeek),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.goalsWeekSummary(sourceWeek),
      });
      // Invalidate current week too since goals were created there
      queryClient.invalidateQueries({
        queryKey: ['goals', 'week'],
      });
    },
  });
}

export function useReorderGoals(week: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReorderRequest) =>
      put<{ detail: string }>(`/goals/week/${week}/reorder`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.goalsWeek(week) });
    },
  });
}

// ============================================================================
// Prefetching Utilities
// ============================================================================

/**
 * Prefetch the next page of documents (for pagination optimization).
 */
export function usePrefetchNextDocumentsPage() {
  const queryClient = useQueryClient();

  return (filters: DocumentFilters) => {
    const nextPageFilters = { ...filters, page: filters.page + 1 };
    queryClient.prefetchQuery({
      queryKey: queryKeys.documents(nextPageFilters),
      queryFn: () => {
        const params = new URLSearchParams();
        params.append('page', nextPageFilters.page.toString());
        params.append('page_size', (nextPageFilters.pageSize ?? 20).toString());
        if (nextPageFilters.search)
          params.append('search', nextPageFilters.search);
        if (nextPageFilters.state)
          params.append('state', nextPageFilters.state);
        if (nextPageFilters.sourceType)
          params.append('source_type', nextPageFilters.sourceType);

        return get<DocumentListResponse>(`/documents?${params.toString()}`);
      },
    });
  };
}

/**
 * Prefetch the next page of priority items (for pagination optimization).
 */
export function usePrefetchNextPriorityPage() {
  const queryClient = useQueryClient();

  return (filters: PriorityFilters) => {
    const nextPageFilters = { ...filters, page: filters.page + 1 };
    queryClient.prefetchQuery({
      queryKey: queryKeys.priorityItems(nextPageFilters),
      queryFn: () => {
        const params = new URLSearchParams();
        params.append('page', nextPageFilters.page.toString());
        params.append('page_size', (nextPageFilters.pageSize ?? 20).toString());
        if (nextPageFilters.status)
          params.append('status', nextPageFilters.status);

        return get<PriorityListResponse>(
          `/integrations/priority?${params.toString()}`
        );
      },
    });
  };
}
